import { IconBuildingBank, IconClock, IconGift, IconPlayerPlay } from "@tabler/icons-react";
import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import { useLocalize } from "../../content/useLocalize";
import { baseRewardForDifficulty } from "../../engine/progression";
import { contractTypeText } from "../../i18n/enumText";
import { rewardTooltip, trustTooltip } from "../../i18n/tooltips";
import type { Client, Contract } from "../../types";
import { Button } from "../common/Button";
import { Card } from "../common/Card";
import { ContractRiskBadge } from "./ContractRiskBadge";
import { DifficultyMeter } from "../common/DifficultyMeter";
import { Tooltip } from "../common/Tooltip";
import { cx } from "../common/cx";
import { formatMoney } from "../common/format";
import { activateOnKey } from "../common/keyActivate";
import { type UrgencyTier } from "./urgency";
import styles from "./MissionCard.module.css";

export interface MissionCardProps {
  contract: Contract;
  client?: Client;
  trust: number;
  recommended?: boolean;
  urgent?: { label: string; bonusPct: number; tier: UrgencyTier } | null;
  onOpen: () => void;
}

export function MissionCard({
  contract,
  client,
  trust,
  recommended = false,
  urgent = null,
  onOpen,
}: MissionCardProps): JSX.Element {
  const { i18n } = useLingui();
  const t = useLocalize();
  const reward = contract.reward ?? baseRewardForDifficulty(contract.difficulty);
  const bonusMoney = urgent ? Math.round(reward.money * urgent.bonusPct) : 0;
  const bonusPctDisplay = urgent ? Math.round(urgent.bonusPct * 100) : 0;
  const tierClass = urgent
    ? { normal: styles.tierNormal, warning: styles.tierWarning, critical: styles.tierCritical }[urgent.tier]
    : undefined;

  return (
    <Card
      interactive
      selected={recommended || urgent !== null}
      className={cx(styles.card, urgent && styles.urgent, tierClass)}
      onClick={onOpen}
      onKeyDown={activateOnKey(onOpen)}
      role="button"
      tabIndex={0}
    >
      <div className={styles.head}>
        <div>
          <h3 className={styles.title}>{t(contract.title)}</h3>
          {client && <span className={styles.client}>{t(client.name)}</span>}
        </div>
        {urgent && (
          <div className={styles.badges}>
            <span className={cx(styles.timer, "mono")}>
              <IconClock size={13} aria-hidden="true" /> {urgent.label}
            </span>
            <span className={styles.bonus}>
              <IconGift size={13} aria-hidden="true" />{" "}
              <Trans>+{bonusPctDisplay}% награды</Trans>
            </span>
          </div>
        )}
      </div>

      <div className={styles.meta}>
        <DifficultyMeter difficulty={contract.difficulty} />
        {contract.riskLevel && <ContractRiskBadge riskLevel={contract.riskLevel} />}
        <span className={styles.taskTag}>{i18n._(contractTypeText(contract.primaryTask))}</span>
        <Tooltip content={i18n._(trustTooltip())} tabIndex={-1}>
          <span className={styles.trust}>
            <IconBuildingBank size={13} aria-hidden="true" /> <Trans>Доверие</Trans>{" "}
            <span className="mono">{trust}</span>
          </span>
        </Tooltip>
      </div>

      <div className={styles.foot}>
        <Tooltip content={i18n._(rewardTooltip())} tabIndex={-1}>
          <span className={cx(styles.money, "mono")}>
            {formatMoney(reward.money)}
            {bonusMoney > 0 && <span className={styles.bonusMoney}> +{formatMoney(bonusMoney)}</span>}
          </span>
        </Tooltip>
        <Button variant="primary" size="small" icon={<IconPlayerPlay size={16} />}>
          <Trans>Взять</Trans>
        </Button>
      </div>
    </Card>
  );
}
